import { get, set, del, keys } from 'idb-keyval';
import { DocumentModel, PaperSize } from '../ast/document.types';
import { migrateDocument } from '../ast/migration';
import { createSampleLabReport, createBlankDocument } from '../ast/factory';

const DOC_PREFIX = 'sds_doc_';
const ACTIVE_DOC_KEY = 'sds_active_doc_id';

export interface ProjectSummary {
  id: string;
  title: string;
  subtitle?: string;
  documentType: 'lab_report' | 'assignment' | 'cover_page' | 'project' | 'custom';
  courseCode?: string;
  studentName?: string;
  sectionCount: number;
  paperSize: PaperSize;
  createdAt: string;
  modifiedAt: string;
  isCoverOnly?: boolean;
}

/**
 * Lists all stored documents with rich metadata for dashboard presentation
 */
export async function listAllProjects(): Promise<ProjectSummary[]> {
  try {
    const allKeys = await keys();
    const docKeys = allKeys.filter((k) => typeof k === 'string' && k.startsWith(DOC_PREFIX)) as string[];
    const summaries: ProjectSummary[] = [];

    for (const key of docKeys) {
      const raw = await get<string>(key);
      if (raw) {
        try {
          const doc: DocumentModel = JSON.parse(raw);
          summaries.push({
            id: doc.metadata.id || key.replace(DOC_PREFIX, ''),
            title: doc.metadata.title || 'Untitled Document',
            subtitle: doc.metadata.subtitle,
            documentType: doc.metadata.documentType || 'custom',
            courseCode: doc.metadata.courseCode,
            studentName: doc.metadata.author?.studentName,
            sectionCount: doc.sections ? doc.sections.filter((s) => s.enabled).length : 0,
            paperSize: doc.pageSettings?.paperSize || 'A4',
            createdAt: doc.metadata.createdAt || new Date().toISOString(),
            modifiedAt: doc.metadata.modifiedAt || doc.metadata.createdAt || new Date().toISOString(),
            isCoverOnly: doc.metadata.documentType === 'cover_page',
          });
        } catch {
          // Ignore corrupted project entries
        }
      }
    }

    // Sort by most recently modified
    return summaries.sort((a, b) => new Date(b.modifiedAt).getTime() - new Date(a.modifiedAt).getTime());
  } catch (error) {
    console.error('Failed to list projects from storage:', error);
    return [];
  }
}

/**
 * Loads a project by ID from IndexedDB
 */
export async function loadProject(id: string): Promise<DocumentModel | null> {
  try {
    const raw = await get<string>(`${DOC_PREFIX}${id}`);
    if (!raw) return null;
    const doc = JSON.parse(raw);
    return migrateDocument(doc);
  } catch (error) {
    console.error(`Failed to load project "${id}":`, error);
    return null;
  }
}

/**
 * Saves or updates a project in IndexedDB
 */
export async function saveProject(doc: DocumentModel): Promise<void> {
  try {
    const updatedDoc: DocumentModel = {
      ...doc,
      metadata: {
        ...doc.metadata,
        modifiedAt: new Date().toISOString(),
      },
    };
    await set(`${DOC_PREFIX}${doc.metadata.id}`, JSON.stringify(updatedDoc));
    await set(ACTIVE_DOC_KEY, doc.metadata.id);
  } catch (error) {
    console.error('Failed to save project:', error);
    throw error;
  }
}

/**
 * Clones/Duplicates an existing project
 */
export async function duplicateProject(id: string): Promise<DocumentModel | null> {
  const original = await loadProject(id);
  if (!original) return null;

  const newId = `doc-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`;
  const clonedDoc: DocumentModel = {
    ...original,
    metadata: {
      ...original.metadata,
      id: newId,
      title: `${original.metadata.title} (Copy)`,
      createdAt: new Date().toISOString(),
      modifiedAt: new Date().toISOString(),
    },
  };

  await saveProject(clonedDoc);
  return clonedDoc;
}

/**
 * Renames a project
 */
export async function renameProject(id: string, newTitle: string): Promise<boolean> {
  const doc = await loadProject(id);
  if (!doc) return false;

  doc.metadata.title = newTitle.trim() || 'Untitled Document';
  doc.metadata.modifiedAt = new Date().toISOString();
  await saveProject(doc);
  return true;
}

/**
 * Deletes a project from storage
 */
export async function deleteProject(id: string): Promise<void> {
  try {
    await del(`${DOC_PREFIX}${id}`);
  } catch (error) {
    console.error(`Failed to delete project "${id}":`, error);
  }
}

/**
 * Creates a brand new document project
 */
export async function createNewProject(
  type: 'lab_report' | 'cover_page' | 'assignment' | 'custom',
  title?: string
): Promise<DocumentModel> {
  let doc: DocumentModel;
  if (type === 'lab_report') {
    doc = createSampleLabReport();
    if (title) doc.metadata.title = title;
  } else {
    doc = createBlankDocument(title || 'New Document');
    doc.metadata.documentType = type;
  }

  await saveProject(doc);
  return doc;
}
